/* @flow */
import type {Dispatch} from 'redux';
import type {Step, JoyrideId} from './onboarding-constants';
import {FALLBACK_STEP, isJoyrideADemo} from './onboarding-constants';
import * as selectors from './onboarding-selectors';
import {
    persistNewJoyrideStepIndex,
    persistJoyrideActiveId,
    checkRedirectNecessary,
    goToPath,
    findResumableStepIndex,
} from './onboarding-helpers';

/*
 * Type definitions
 */

type GetState = () => any;

export type JoyrideAction =
    | {type: 'JOYRIDE_DISABLED'}
    | {type: 'JOYRIDE_ENABLED'}
    | {type: 'JOYRIDE_STARTED', payload: {joyrideId: JoyrideId}}
    | {type: 'JOYRIDE_RESTARTED', payload: {joyrideId: JoyrideId}}
    | {type: 'JOYRIDE_ACTIVE_JOYRIDE_STOPPED'}
    | {type: 'JOYRIDE_TEMPORARILY_STOPPED'}
    | {type: 'JOYRIDE_STEP_INDEX_CHANGED', payload: {joyrideId: JoyrideId, stepIndex: number}}
    | {type: 'persist/REHYDRATE', payload: Object};

/*
 * Type Constants
 */

export const JOYRIDE_DISABLED = 'JOYRIDE_DISABLED';
export const JOYRIDE_ENABLED = 'JOYRIDE_ENABLED';
export const JOYRIDE_STARTED = 'JOYRIDE_STARTED';
export const JOYRIDE_RESTARTED = 'JOYRIDE_RESTARTED';
export const JOYRIDE_ACTIVE_JOYRIDE_STOPPED = 'JOYRIDE_ACTIVE_JOYRIDE_STOPPED';
export const JOYRIDE_TEMPORARILY_STOPPED = 'JOYRIDE_TEMPORARILY_STOPPED';
export const JOYRIDE_STEP_INDEX_CHANGED = 'JOYRIDE_STEP_INDEX_CHANGED';

/*
 * Actions
 */

export const disableJoyride = (): JoyrideAction => ({type: JOYRIDE_DISABLED});
export const enableJoyride = (): JoyrideAction => ({type: JOYRIDE_ENABLED});
export const startJoyrideById = (joyrideId: JoyrideId): JoyrideAction => ({
    type: JOYRIDE_STARTED,
    payload: {joyrideId},
});
export const restartJoyrideById = (joyrideId: JoyrideId): JoyrideAction => ({
    type: JOYRIDE_RESTARTED,
    payload: {joyrideId},
});
export const stopActiveJoyride = (): JoyrideAction => ({type: JOYRIDE_ACTIVE_JOYRIDE_STOPPED});
export const temporarilyStopJoyride = (): JoyrideAction => ({type: JOYRIDE_TEMPORARILY_STOPPED});
export const changeJoyrideStepIndexById = (payload: {
    joyrideId: JoyrideId,
    stepIndex: number,
}): JoyrideAction => ({
    type: JOYRIDE_STEP_INDEX_CHANGED,
    payload,
});

/*
 * Action Creators
 */

/**
 * Convenience function to start a joyride by id, but first persist its id
 * to the database, and _then_ dispatch the action to start
 * @param  {string} joyrideId     Joyride id to start and persist to the server
 * @return {function}             Returns the actionCreator that was generated here
 */
export function startAndPersistJoyrideById(joyrideId: JoyrideId) {
    return function(dispatch: Dispatch<*>, getState: GetState) {
        const state = getState();
        const index = selectors.getJoyrideStepIndex(state, joyrideId);
        const step = selectors.getJoyrideStep(state, joyrideId, index) || FALLBACK_STEP;
        const stepUri = step.uri;
        if (checkRedirectNecessary(stepUri)) {
            persistJoyrideActiveId(joyrideId).then(() => goToPath(stepUri));
        } else if (step.isResumable !== false) {
            dispatch(startJoyrideById(joyrideId));
            persistJoyrideActiveId(joyrideId);
        } else {
            const resumableStepIndex: number = findResumableStepIndex(joyrideId, index);
            dispatch(changeJoyrideStepIndexById({joyrideId, stepIndex: resumableStepIndex}));
            dispatch(startJoyrideById(joyrideId));
            persistJoyrideActiveId(joyrideId);
        }
    };
}

export function restartAndPersistJoyrideById(joyrideId: JoyrideId) {
    return function(dispatch: Dispatch<*>) {
        persistNewJoyrideStepIndex(joyrideId, 0);
        persistJoyrideActiveId(joyrideId);

        dispatch(restartJoyrideById(joyrideId));
    };
}

/**
 * Convenience function to stop the active joyride and persist that knowledge
 * to the server before doing so.
 *
 * @param  {object} step          Step object defined in our joyride-constants
 *                                that potentially contains callbacks
 * @return {function}             Returns the actionCreator that was generated here
 */
export function stopAndPersistActiveJoyride(step: Step) {
    return function(dispatch: Dispatch<*>, getState: GetState) {
        const state = getState();
        const joyrideId = selectors.getActiveJoyrideId(state);
        const index = selectors.getJoyrideStepIndex(state, joyrideId);
        const resumableStepIndex: number = findResumableStepIndex(joyrideId, index);

        dispatch(stopActiveJoyride());

        // $FlowFixMe upgrading Flow to v0.92.1 on web
        if (typeof step.onCloseCallback === 'function') {
            // $FlowFixMe upgrading Flow to v0.92.1 on web
            step.onCloseCallback(dispatch);
        }

        if (index !== resumableStepIndex) {
            dispatch(changeJoyrideStepIndexById({joyrideId, stepIndex: resumableStepIndex}));
            // We don't need to persist resumableStepIndex to the server, because
            // we are always checking if a step is resumable before persisting it
            // in the first place, so we shouldn't have a non-resumable step stored.
        }

        // We're purposefully not getting a callback and _then_ stopping the
        // active joyride, as that causes some blips on the screen which look
        // bad.
        persistJoyrideActiveId(null);
    };
}

/**
 * Function to persist the new stepIndex to the database when the 'back' button
 * is clicked on a step. It will also optionally call the step's `onStepNext`
 * callback if one exists
 *
 * @param  {object} step        Step object defined in our joyride-constants
 *                              that potentially contains callbacks
 * @param  {number} stepIndex   Taken from react-joyride, this is joyride of the
 *                              current step, _before_ we move to the next step
 * @return {function}           Consumable function to call the action
 */
export function completeJoyrideStep(step: Step, stepIndex: number) {
    return function(dispatch: Dispatch<*>, getState: GetState) {
        const state = getState();
        const activeJoyrideId = selectors.getActiveJoyrideId(state);
        const nextIndex: number = stepIndex + 1;
        const nextStep: Step =
            selectors.getJoyrideStep(state, activeJoyrideId, nextIndex) || FALLBACK_STEP;
        const stepUri = nextStep.uri;

        // $FlowFixMe upgrading Flow to v0.92.1 on web
        if (typeof step.onStepNextCallback === 'function') {
            // $FlowFixMe upgrading Flow to v0.92.1 on web
            step.onStepNextCallback(dispatch, step, activeJoyrideId, nextIndex);
        } else if (checkRedirectNecessary(stepUri)) {
            dispatch(temporarilyStopJoyride());
            persistNewJoyrideStepIndex(activeJoyrideId, nextIndex).then(() => {
                goToPath(stepUri);
            });
        } else {
            dispatch(
                changeJoyrideStepIndexById({joyrideId: activeJoyrideId, stepIndex: nextIndex})
            );

            if (nextStep.isResumable !== false) {
                persistNewJoyrideStepIndex(activeJoyrideId, nextIndex);
            }
        }

        // This is different from the 'onStepNextCallback' in that it won't
        // intercept the default behavior, but rather just trigger a side effect
        // $FlowFixMe upgrading Flow to v0.92.1 on web
        if (typeof step.onStepCompleteCallback === 'function') {
            // $FlowFixMe upgrading Flow to v0.92.1 on web
            step.onStepCompleteCallback();
        }
    };
}

/**
 * Function to persist the new stepIndex to the database when the 'back' button
 * is clicked on a step. It will also optionally call the step's `onStepPrevious`
 * callback if one exists
 *
 * @param  {object} step        Step object defined in our joyride-constants
 *                              that potentially contains callbacks
 * @param  {number} stepIndex   Taken from react-joyride, this is joyride of the
 *                              current step, _before_ we move to the previous step
 * @return {function}           Consumable function to call the action
 */
export function moveToPreviousStep(step: Step, stepIndex: number) {
    return function(dispatch: Dispatch<*>, getState: GetState) {
        const state = getState();
        const activeJoyrideId = selectors.getActiveJoyrideId(state);
        const prevIndex: number = stepIndex - 1;
        const prevStep =
            selectors.getJoyrideStep(state, activeJoyrideId, prevIndex) || FALLBACK_STEP;
        const stepUri = prevStep.uri;

        if (checkRedirectNecessary(stepUri)) {
            dispatch(temporarilyStopJoyride());
            persistNewJoyrideStepIndex(activeJoyrideId, prevIndex).then(() => {
                goToPath(stepUri);
            });
            // $FlowFixMe upgrading Flow to v0.92.1 on web
        } else if (typeof step.onStepPreviousCallback === 'function') {
            // $FlowFixMe upgrading Flow to v0.92.1 on web
            step.onStepPreviousCallback(dispatch, step, activeJoyrideId, prevIndex);
        } else {
            dispatch(
                changeJoyrideStepIndexById({
                    joyrideId: activeJoyrideId,
                    stepIndex: prevIndex,
                })
            );
            if (prevStep.isResumable !== false) {
                persistNewJoyrideStepIndex(activeJoyrideId, prevIndex);
            }
        }
    };
}

/**
 * Stops the active joyride and marks it as complete. Since this is technically
 * triggered by a 'Back' button click, we need to override the joyride step
 * which is why the stepIndexById is changed and persisted to the server here.
 *
 * We'll then persist the stopped information to the server and call it a day
 * @param  {integer} stepIndex      Step index of the final step in the joyride
 * @return {void}
 */
export function finishActiveJoyrideAndExit(stepIndex: number) {
    return function(dispatch: Dispatch<*>, getState: GetState) {
        const state = getState();
        const activeJoyrideId = selectors.getActiveJoyrideId(state);
        const nextIndex = stepIndex + 1;

        dispatch(stopActiveJoyride());
        dispatch(changeJoyrideStepIndexById({joyrideId: activeJoyrideId, stepIndex: nextIndex}));
        persistNewJoyrideStepIndex(activeJoyrideId, nextIndex);
        persistJoyrideActiveId(null);
    };
}

/**
 * Attempts to start the "Next" demo joyride, which
 * is equivalent to the next non-completed joyride in our list of joyrides.
 * @return {void}
 */
export function startNextDemoJoyride() {
    return function(dispatch: Function, getState: GetState) {
        const state = getState();
        const nextJoyrideId = selectors.getNextJoyrideId(state);

        dispatch(stopActiveJoyride());
        if (nextJoyrideId) {
            dispatch(startAndPersistJoyrideById(nextJoyrideId));
        }
    };
}

/**
 * Stops the active joyride, and if the joyride is a demo, dispatches
 * an action to start the next joyride.
 *
 * @return {void}
 */
export function finishActiveJoyride() {
    return function(dispatch: Function, getState: GetState) {
        const state = getState();
        const joyrideId = selectors.getActiveJoyrideId(state);
        dispatch(stopActiveJoyride());

        if (isJoyrideADemo(joyrideId)) {
            dispatch(startNextDemoJoyride());
        }
    };
}
